import {
  Code2,
  Smartphone,
  Building2,
  Brain,
  Cloud,
  BarChart3,
  Workflow,
  RefreshCw,
  Package,
  Network,
  Wrench,
  Lightbulb,
  ShieldCheck,
  Users,
  Target,
  Cpu,
  MessagesSquare,
  BadgeCheck,
  Handshake,
  Landmark,
  CircleDollarSign,
  Umbrella,
  HeartPulse,
  ShoppingCart,
  RadioTower,
  Factory,
} from "lucide-react";

export const COMPANY = {
  name: "Vaskion Technologies Private Limited",
  shortName: "Vaskion Technologies",
  tagline: "Innovate • Build • Transform.",
  email: "info@vaskiontech.com",
  website: "www.vaskiontech.com",
  websiteUrl: "https://www.vaskiontech.com",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Solutions", to: "/solutions" },
  { label: "Technologies", to: "/technologies" },
  { label: "Industries", to: "/industries" },
  { label: "About", to: "/about" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

export const SERVICES = [
  {
    icon: Code2,
    title: "Custom Software Development",
    description:
      "Purpose-built software designed around your workflows, goals and long-term product vision.",
  },
  {
    icon: Smartphone,
    title: "Web and Mobile Application Development",
    description:
      "Responsive web platforms and mobile applications engineered for performance and usability.",
  },
  {
    icon: Building2,
    title: "Enterprise Application Development",
    description:
      "Robust enterprise systems that support complex operations, integrations and scale.",
  },
  {
    icon: Brain,
    title: "AI and Machine Learning Solutions",
    description:
      "Applied AI and ML capabilities that turn data into intelligent, automated business outcomes.",
  },
  {
    icon: Cloud,
    title: "Cloud and DevOps",
    description:
      "Cloud architecture, migration and DevOps practices for reliable, efficient delivery.",
  },
  {
    icon: BarChart3,
    title: "Data Analytics",
    description:
      "Analytics pipelines and dashboards that make business data clear, trusted and actionable.",
  },
  {
    icon: Workflow,
    title: "Business Process Automation",
    description:
      "Automation of repetitive processes to reduce manual effort, errors and operational cost.",
  },
  {
    icon: RefreshCw,
    title: "Digital Transformation",
    description:
      "Structured modernization of legacy processes and systems into digital-first operations.",
  },
  {
    icon: Package,
    title: "Product Engineering",
    description:
      "End-to-end product engineering from concept and architecture to launch and iteration.",
  },
  {
    icon: Network,
    title: "System Integration",
    description:
      "Seamless integration of applications, APIs and data across your technology landscape.",
  },
  {
    icon: Wrench,
    title: "Application Maintenance and Support",
    description:
      "Ongoing maintenance, monitoring and enhancement to keep applications healthy and current.",
  },
  {
    icon: Lightbulb,
    title: "IT Consulting",
    description:
      "Practical technology advisory to help you make confident, well-informed decisions.",
  },
  {
    icon: ShieldCheck,
    title: "Managed Services",
    description:
      "Managed operation of your technology stack with clear accountability and continuity.",
  },
  {
    icon: Users,
    title: "Resource Augmentation",
    description:
      "Skilled engineering talent that integrates with your team and ways of working.",
  },
];

export const SOLUTIONS = [
  {
    title: "AI-Powered Business Solutions",
    description:
      "Embed intelligence into everyday operations — from document understanding and prediction to smart assistants that support real business decisions.",
  },
  {
    title: "Enterprise Modernization",
    description:
      "Re-architect legacy applications into modular, maintainable systems without disrupting the business that depends on them.",
  },
  {
    title: "Cloud Transformation",
    description:
      "Move workloads to the cloud with a clear strategy for security, cost control and operational resilience.",
  },
  {
    title: "Business Process Automation",
    description:
      "Identify manual bottlenecks and replace them with reliable, auditable automated workflows.",
  },
  {
    title: "Data-Driven Decision Making",
    description:
      "Build the data foundations — pipelines, models and dashboards — that let leaders act on evidence rather than instinct.",
  },
  {
    title: "Secure and Scalable Digital Platforms",
    description:
      "Platforms engineered from the ground up for security, compliance readiness and growth in users and data.",
  },
];

export const TECHNOLOGIES = [
  { name: "Java", note: "Enterprise-grade backend engineering" },
  { name: "Spring Boot", note: "Production-ready microservices and APIs" },
  { name: "React", note: "Modern, responsive user interfaces" },
  { name: "Python", note: "AI, data engineering and automation" },
  { name: "FastAPI", note: "High-performance API services" },
  { name: "PostgreSQL", note: "Reliable relational data platforms" },
  { name: "Cloud Platforms", note: "Scalable infrastructure on leading clouds" },
  { name: "DevOps", note: "CI/CD, containers and infrastructure as code" },
  { name: "Artificial Intelligence", note: "Applied AI for real business workflows" },
  { name: "Machine Learning", note: "Models trained for prediction and insight" },
  { name: "Data Analytics", note: "Pipelines, warehouses and dashboards" },
];

export const INDUSTRIES = [
  {
    icon: Landmark,
    title: "Banking",
    description:
      "Secure digital channels, core-system integration and automation for banking operations.",
  },
  {
    icon: CircleDollarSign,
    title: "Financial Services",
    description:
      "Platforms for lending, payments and wealth workflows with strong data integrity.",
  },
  {
    icon: Umbrella,
    title: "Insurance",
    description:
      "Digital policy, claims and underwriting experiences built for accuracy and speed.",
  },
  {
    icon: HeartPulse,
    title: "Healthcare",
    description:
      "Interoperable, privacy-conscious software for providers and health-tech products.",
  },
  {
    icon: ShoppingCart,
    title: "E-commerce",
    description:
      "Scalable storefronts, order management and personalised shopping experiences.",
  },
  {
    icon: RadioTower,
    title: "Telecommunications",
    description:
      "Customer portals, billing integrations and operational systems for telecom providers.",
  },
  {
    icon: Factory,
    title: "Manufacturing",
    description:
      "Connected systems for production visibility, quality and supply-chain coordination.",
  },
];

export const WHY_CHOOSE = [
  {
    icon: Target,
    title: "Business-Focused Engineering",
    description:
      "Every technical decision is anchored to a business outcome, not technology for its own sake.",
  },
  {
    icon: ShieldCheck,
    title: "Secure and Scalable Architecture",
    description:
      "Security and scale are designed in from the first sprint, not bolted on afterwards.",
  },
  {
    icon: Cpu,
    title: "Modern Technology Expertise",
    description:
      "A current, carefully chosen stack across backend, frontend, cloud, data and AI.",
  },
  {
    icon: MessagesSquare,
    title: "Transparent Collaboration",
    description:
      "Clear communication, visible progress and honest conversations at every stage.",
  },
  {
    icon: BadgeCheck,
    title: "Quality-Driven Delivery",
    description:
      "Disciplined engineering practices with reviews, testing and documentation built in.",
  },
  {
    icon: Handshake,
    title: "Long-Term Technology Partnership",
    description:
      "We build relationships beyond a single project, supporting your roadmap as it evolves.",
  },
];

export const VALUES = [
  {
    title: "Innovation",
    description: "We explore better ways to solve problems and apply new technology with purpose.",
  },
  {
    title: "Integrity",
    description: "We are honest about what is possible, what it costs and what it takes.",
  },
  {
    title: "Quality",
    description: "We hold our work to standards we would expect as clients ourselves.",
  },
  {
    title: "Security",
    description: "We treat every system we build as something worth protecting.",
  },
  {
    title: "Collaboration",
    description: "We work as an extension of your team, not apart from it.",
  },
  {
    title: "Continuous Improvement",
    description: "We review, refine and raise the bar with every engagement.",
  },
];

export const HOW_WE_WORK = [
  {
    step: "01",
    title: "Discover and Define",
    description:
      "We begin by understanding your business context, constraints and success criteria before proposing any solution.",
  },
  {
    step: "02",
    title: "Design and Architect",
    description:
      "We shape the solution architecture, user experience and delivery plan with clear milestones and ownership.",
  },
  {
    step: "03",
    title: "Build and Validate",
    description:
      "We develop in iterative cycles with regular demos, reviews and testing so there are no surprises.",
  },
  {
    step: "04",
    title: "Deploy and Support",
    description:
      "We launch carefully, monitor closely and stay engaged with maintenance and continuous improvement.",
  },
];

export const SERVICE_OPTIONS = SERVICES.map((s) => s.title);

export const COUNTRY_CODES = [
  { code: "+91", label: "India (+91)" },
  { code: "+1", label: "United States / Canada (+1)" },
  { code: "+44", label: "United Kingdom (+44)" },
  { code: "+61", label: "Australia (+61)" },
  { code: "+65", label: "Singapore (+65)" },
  { code: "+971", label: "United Arab Emirates (+971)" },
  { code: "+49", label: "Germany (+49)" },
  { code: "+81", label: "Japan (+81)" },
  { code: "+974", label: "Qatar (+974)" },
  { code: "+966", label: "Saudi Arabia (+966)" },
];

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1718844054440-22acf5d5c8f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMHRlY2hub2xvZ3l8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzg3OTAzMzE4fDA&ixlib=rb-4.1.0&q=85",
  architecture:
    "https://images.unsplash.com/photo-1498262257252-c282316270bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzg3OTAzMzExfDA&ixlib=rb-4.1.0&q=85",
  angularRed:
    "https://images.unsplash.com/photo-1505508520564-7e2642683d77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdlb21ldHJpY3xlbnwwfHx8cmVkfDE3ODc5MDMzMTh8MA&ixlib=rb-4.1.0&q=85",
  corporate:
    "https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzg3OTAzMzExfDA&ixlib=rb-4.1.0&q=85",
  building:
    "https://images.unsplash.com/photo-1619985730446-20b8ba0cb436?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZ3xlbnwwfHx8YmxhY2tfYW5kX3doaXRlfDE3ODc5MDMzMTh8MA&ixlib=rb-4.1.0&q=85",
};
